import React, { useMemo, useRef, useState } from 'react';
import { getLocale } from 'umi';
import useIntlExtend from '@/pages/intelligence/hooks/useIntlExtend';
import { isTooltipShowAll } from '@intelligence/utils/common';
import ChartsIndex from '@/pages/intelligence/components/ChartsComponents/ChartsIndex';
// import { RenderType } from '@/pages/intelligence/pages/_config/topchartsInterface';
import useBarCharts from '@/pages/intelligence/components/Chart/hooks/useBarCharts';
import useLineCharts from '@/pages/intelligence/components/Chart/hooks/useLineCharts';
import useSinglePieCharts from '@/pages/intelligence/components/Chart/hooks/useSinglePieCharts';
import { showTimeByDateType } from '@/pages/intelligence/utils/commonUtils';
// import { getIfPercentage } from '@/pages/intelligence/utils/common';
import { ChartType } from '@/pages/intelligence/pages/TopCharts/_config/enums';
import type { IChartsIndexInstance } from '@intelligence/components/ChartsComponents/ChartsIndex';
import { DATASOURCE_NAME } from '@intelligence/components/DataSourceComponents';
import {
  buildVisualMetricAxisInfo,
  findMetricInTableList,
  getDualAxisHeaderTexts,
  getMetricDisplayText,
} from '@intelligence/pages/TopCharts/utils/multiValueMetricUtils';
import './index.scss';

const LINE_LEGEND_SOURCE_NAME: Record<string, string> = {
  source_steam: 'Steam',
  steamcharts: 'Steam',
  vginsights: 'VGI',
  gamalytic: 'Gamalytic',
  alinea: 'Alinea Analytics',
};

const prependDataSourceName = (name: string, dataSource?: string): string => {
  if (!dataSource) return name;
  const sourceName =
    LINE_LEGEND_SOURCE_NAME[dataSource] || (DATASOURCE_NAME as any)?.[dataSource];
  if (!sourceName) return name;

  if (sourceName === 'Steam') {
    return `${sourceName} ${name.replace(/^Steam\s+/i, '')}`;
  }
  return `${sourceName} ${name}`;
};

const COUNTRIES_PIE_COUNTRY_NAME_LOCALE_PAGE_IDS = [
  'digitalRevenue',
  'gsd',
  'newzoo',
  'newzooMetrics',
];
const COUNTRIES_PIE_LAYOUT_PAGE_IDS = [
  'gsd',
  'newzoo',
  'newzooMetrics',
  'noUpdate',
  'newzooMetrics—player_share',
  'ampere—ampere_mau',
  'newzooMetrics—mau',
];
const COUNTRIES_PIE_LEGEND_GAP = 20;
const COUNTRIES_TOOLTIP_CONFIG = {
  appendToBody: true,
  extraCssText: 'z-index: 9999999;',
};

// 每个榜单，组装好各类可视化需要的数据，传递给可视化统一组件
const Visualis: React.FC<any> = (props: any) => {
  const {
    chartType = ChartType.Line,
    dateType,
    startDate,
    endDate,
    lineData: lineDataTemp = [], // 折线 数据
    isNodata,
    imageType,
    metric,
    titleNow,
    chartLoading,
    setChartLoading,
    topChartsType,
    marketLineLoading,
    ifUseMarketLineLoading = false,
    tableData,
    barData: barDataTemp,
    calOtherList,
    gameName,
    regionsCountries,
    baseReleaseTime,
    earliestPeriod,
    regionSegVal,
    lineSeriesFormatter,
    isStepLine,
    lineMetricName,
    visualisLineTopChartsType,
    visualisBarTopChartsType,
    enableAutoBarWidth,
    visualisPieTopChartsType,
    isNaNeedMax,
    byDevice = false,
    isStackLine,
    barSeriesFormatter,
    pieSeriesFormatter,
    barNameListFormatter,
    pieData: pieDataTemp,
    isCountriesPage = false,
    isPercentage,
    isPercentageDecimal = false,
    visuailsMetricValues: visuailsMetricValuesTemp = [],
    tableMetricList = [],
    chartNodataMsg,
    legendSelected,
    secondTitleRight,
    lineSourceName,
    ifLineChartLegendOn = false,
    lineSourceIcon,
    sourceList,
    yAxisConfig,
    /**
     * Countries UserEngagement 堆叠折线图 tooltip 展示 all 合计值
     */
    stackLineTooltipShowAll,
    /**
     * 展示的图表图形元素数量
     */
    showChartsItemNum = 5,
    /**
     * tooltip 额外配置
     */
    tooltip,
    gridConfig,
    lineXAxisData,
  } = props;

  // console.log('Visualis`````', props);
  const { formatMessage } = useIntlExtend();
  const locale = getLocale(); // 中英切换方法
  const cardRef = useRef<HTMLDivElement | null>(null);
  const chartsInstance = useRef<IChartsIndexInstance>(null);
  const [earliestClickValue, setEarliestClickValue] = useState<any>('');
  // const isPercentage = useMemo(() => {
  //   switch (renderType) {
  //     case RenderType.GROWTH_ONLY:
  //       return true;
  //     default:
  //       return false;
  //   }
  // }, [topChartsType, renderType]);

  const visuailsMetricValues = useMemo(
    () =>
      buildVisualMetricAxisInfo(visuailsMetricValuesTemp, {
        tableMetricList,
        formatMessage,
        formatLegendText: (metricObj) =>
          getMetricDisplayText(metricObj, formatMessage),
        formatAxisText: (metricId, text, metricObj) => {
          if (topChartsType !== 'allMetrics—allMetrics') return text;
          const dataSource =
            metricObj?.dataSource ||
            findMetricInTableList(metricId, tableMetricList)?.dataSource;
          return prependDataSourceName(text, dataSource);
        },
      }),
    [topChartsType, visuailsMetricValuesTemp, tableMetricList, locale, formatMessage],
  );

  const axisHeaderTexts = useMemo(
    () =>
      topChartsType === 'allMetrics—allMetrics'
        ? getDualAxisHeaderTexts(visuailsMetricValues)
        : [],
    [topChartsType, visuailsMetricValues],
  );
  const showExternalAxisTitles = axisHeaderTexts.length === 2;

  // 拿到数据，先按规则要求处理，再传给可视化使用
  const lineData = useMemo(() => {
    if (lineDataTemp && Array.isArray(lineDataTemp) && lineDataTemp?.length > 0) {
      return lineDataTemp?.map((item: any) => {
        if (item.date) {
          return {
            ...item,
            date: showTimeByDateType(item.date, dateType?.toLocaleLowerCase()),
          };
        }
        return item;
      });
    }
    return lineDataTemp;
  }, [JSON.stringify(lineDataTemp)]);

  // 拿到数据，先按规则要求处理，再传给可视化使用
  const barData = useMemo(() => {
    if (barDataTemp && Array.isArray(barDataTemp) && barDataTemp?.length > showChartsItemNum) {
      return barDataTemp?.slice(0, showChartsItemNum);
    }
    return barDataTemp;
  }, [JSON.stringify(barDataTemp)]);

  // 拿到数据，先按规则要求处理，再传给可视化使用
  const pieData = useMemo(() => {
    return pieDataTemp;
  }, [JSON.stringify(pieDataTemp)]);

  // arr.sort((a,b) => a - b);

  const isHoverPie = useMemo(() => {
    if (isCountriesPage) return false;
    return imageType == 'combine';
  }, [topChartsType, imageType, chartType]);
  const localizedOthersName = formatMessage({ id: 'dbi_others' });
  const tooltipOthersNames = ['others', 'Others', localizedOthersName];
  const countriesSeriesNameFormatter = (name: string) => {
    if (typeof name !== 'string') {
      return name;
    }

    if (name.trim().toLowerCase() === 'others') {
      return localizedOthersName;
    }

    return name;
  };

  const barNameList = useMemo(() => {
    return barNameListFormatter?.({ barData }) || [];
  }, [JSON.stringify(barData)]);

  const barSeriesData = useMemo(() => {
    return (
      barSeriesFormatter?.({
        barData,
        legendMetric: metric,
        regionsCountries,
        sourceListParams: sourceList,
        localizedOthersName,
      }) || []
    );
  }, [JSON.stringify(barData), metric, chartType, localizedOthersName]);

  const pieListData = useMemo(() => {
    if (isHoverPie) {
      return (
        pieSeriesFormatter?.({
          pieData,
          legendMetric: metric,
          isCountriesPage,
          regionsCountries,
          byDevice,
          titleNow,
        }) || []
      );
    }
    return [];
  }, [JSON.stringify(pieData), metric, isHoverPie]);

  // 按设备区分时，是否为需要展示 all 合计值。ampere 占比指标不展示
  const isCurrentMetricTooltipShowAll = useMemo(() => isTooltipShowAll(metric), [metric]);

  const enableCountriesPieCountryNameLocale = useMemo(() => {
    return (
      chartType === ChartType.Pie &&
      isCountriesPage &&
      COUNTRIES_PIE_COUNTRY_NAME_LOCALE_PAGE_IDS.includes(topChartsType)
    );
  }, [chartType, isCountriesPage, topChartsType]);
  const shouldUseCountriesPieLayoutConfig = isCountriesPage && COUNTRIES_PIE_LAYOUT_PAGE_IDS.includes(topChartsType);
  const chartTooltipConfig = useMemo(() => {
    if (shouldUseCountriesPieLayoutConfig) {
      return {
        ...(tooltip || {}),
        ...COUNTRIES_TOOLTIP_CONFIG,
      };
    }
    return tooltip;
  }, [shouldUseCountriesPieLayoutConfig, tooltip]);
  let pieLegendGap: number | undefined;
  if (shouldUseCountriesPieLayoutConfig) {
    pieLegendGap = COUNTRIES_PIE_LEGEND_GAP;
  }

  // 获取柱状图hooks
  const { barOption } = useBarCharts({
    seriesData: barSeriesData || [],
    barOrPieData: (byDevice ? barData : barSeriesData) || [],
    setChartLoading,
    // setChartLoading: chartType === ChartType.Bar ? setChartLoading : () => {},
    titleNow,
    barNameList,
    topChartsType: visualisBarTopChartsType,
    cardWidth: cardRef?.current?.clientWidth,
    chartType,
    ifPercentage: isPercentage, // getIfPercentage(metric),
    isPercentageDecimal,
    regionSegVal,
    imageType: regionSegVal ? regionSegVal : imageType,
    barCurSeries: barSeriesData,
    isStackBar: byDevice,
    ifHasOtherHeadContent: byDevice && isCurrentMetricTooltipShowAll,
    startDate: showTimeByDateType(startDate, dateType?.toLocaleLowerCase()),
    endDate: showTimeByDateType(endDate, dateType?.toLocaleLowerCase()),
    isShowLegend: byDevice && imageType === 'combine',
    useMarketFilterApiKey: topChartsType === 'gsd' ? topChartsType : '',
    ifNoBarImg: visualisBarTopChartsType === 'pcconsole-country-bar-pcu',
    enableAutoBarWidth,
    tooltipExtraConfig: chartTooltipConfig,
  });

  const pieSeriesData = useMemo(() => {
    return pieSeriesFormatter?.({
      list: barData,
      regionsCountries,
      legendMetric: metric,
      calOtherList,
      allData: tableData,
      enableCountryNameLocale: enableCountriesPieCountryNameLocale,
      localizedOthersName,
    });
  }, [
    JSON.stringify(tableData),
    metric,
    JSON.stringify(barData),
    enableCountriesPieCountryNameLocale,
    locale,
    localizedOthersName,
  ]);

  // console.log(pieSeriesData);
  // 获取饼图hooks
  const { pieOption } = useSinglePieCharts({
    pieData: pieSeriesData,
    pieTitle: gameName,
    setChartLoading,
    // setChartLoading: chartType === ChartType.Pie ? setChartLoading : () => {},
    chartLoading,
    chartType,
    chartsInstance,
    titleNow,
    secondTitleRight,
    startDate: showTimeByDateType(startDate, dateType?.toLocaleLowerCase()),
    endDate: showTimeByDateType(endDate, dateType?.toLocaleLowerCase()),
    tooltip: chartTooltipConfig,
    pieLegendGap,
  });

  const onChartEarliestClick = (data?: any) => {
    setEarliestClickValue(data?.value);
  };

  const lineSeriesData = useMemo(() => {
    return lineSeriesFormatter?.({
      list: lineData || [],
      legendMetric: metric,
      dateType,
      earliestClickValue,
      titleNow,
      regionsCountries,
      isPercentLine: isPercentage,
      visuailsMetricValues,
      localizedOthersName,
      formatMessage,
    });
  }, [
    lineData,
    metric,
    earliestClickValue,
    visuailsMetricValues,
    locale,
    localizedOthersName,
    formatMessage,
    lineSeriesFormatter,
    dateType,
    titleNow,
    regionsCountries,
    isPercentage,
  ]);

  // 获取折线图hooks
  const { lineOption } = useLineCharts({
    lineXAxisData,
    legendSelected,
    isStackLine,
    lineData: lineSeriesData,
    metric: lineMetricName,
    topChartsType: visualisLineTopChartsType,
    imageType,
    titleNow,
    chartType,
    setChartLoading,
    // setChartLoading:
    //   chartType === ChartType.Line || chartType === ChartType.StackedLine
    //     ? setChartLoading
    //     : () => {},
    dateType,
    isPercentLine: isPercentage,
    isNaNeedMax,
    isStep: isStepLine,
    /**
     * 折线图 tooltip 需展示加总的条件
     * 1. 分设备平台的折线图
     * 2. countries ampere 的堆叠折线图
     */
    ifHasOtherHeadContent: byDevice || stackLineTooltipShowAll,
    baseReleaseTime: showTimeByDateType(baseReleaseTime, dateType?.toLocaleLowerCase()),
    earliestPeriod: showTimeByDateType(earliestPeriod, dateType?.toLocaleLowerCase()),
    // hasMarkLine: true,
    startDate: showTimeByDateType(startDate, dateType?.toLocaleLowerCase()),
    endDate: showTimeByDateType(endDate, dateType?.toLocaleLowerCase()),
    isTwoYAxis:
      topChartsType == 'allMetrics—allMetrics' &&
      visuailsMetricValuesTemp?.length === 2,
    useExternalAxisTitles: topChartsType === 'allMetrics—allMetrics',
    visuailsMetricValues,
    secondTitleRight,
    lineSourceName,
    lineSourceIcon,
    yAxisConfig,
    tooltip: chartTooltipConfig,
    seriesNameFormatter: isCountriesPage ? countriesSeriesNameFormatter : undefined,
    shouldMoveTooltipOthersLast: isCountriesPage,
    tooltipOthersNames,
    gridConfig,
  });

  const isNoVisualData =
    isNodata ||
    (chartType === ChartType.Bar && !barSeriesData?.length) ||
    (chartType === ChartType.Line && lineSeriesData?.noData) ||
    (chartType === ChartType.StackedLine && lineSeriesData?.noData) ||
    (chartType === ChartType.Pie && !isHoverPie && !pieSeriesData?.length);

  return (
    <>
      <div className="card-line" ref={cardRef}>
        {showExternalAxisTitles && (
          <div className="pc-stats-axis-titles">
            <span className="pc-stats-axis-titles__item pc-stats-axis-titles__item--left">
              {axisHeaderTexts[0]}
            </span>
            <span className="pc-stats-axis-titles__item pc-stats-axis-titles__item--right">
              {axisHeaderTexts[1]}
            </span>
          </div>
        )}
        <ChartsIndex
          echartHight={350}
          nodataMsg={chartNodataMsg}
          ref={chartsInstance}
          chartLoading={ifUseMarketLineLoading ? marketLineLoading : chartLoading}
          lineOption={lineOption}
          barOption={barOption}
          pieOption={isHoverPie ? null : pieOption}
          pieData={pieListData}
          chartType={chartType}
          isNodata={isNoVisualData}
          onChartEarliestClick={onChartEarliestClick}
          isHoverPie={isHoverPie}
          metricText={titleNow}
          startDate={showTimeByDateType(startDate, dateType?.toLocaleLowerCase())}
          endDate={showTimeByDateType(endDate, dateType?.toLocaleLowerCase())}
          topChartsType={visualisPieTopChartsType}
          byDevice={byDevice}
          secondTitleRight={byDevice ? null : secondTitleRight}
          ifLineChartLegendOn={ifLineChartLegendOn}
        />
      </div>
    </>
  );
};

export default Visualis;
